import React, { useState, useRef, useEffect } from "react";
import { Doughnut, Bar, Line } from "react-chartjs-2";
import article from "./TaxifyContent";
import WaldoAd from '../AdComponent';
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
} from "chart.js";

Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
);

const PostTaxReturnCalculator = () => {
  const content = article.find((item) => item.id === 6);
  const [mode, setMode] = useState("stocks");
  const [activeTab, setActiveTab] = useState("calculator");
  const [isMobile, setIsMobile] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const [inputs, setInputs] = useState({
    // Investment Basics
    initialInvestment: "10000",
    currentValue: "15000",
    investmentType: "stocks",
    holdingPeriod: "365", // days
    
    // Return Scenarios
    expectedReturn: "12",
    inflationRate: "2.5",
    investmentHorizon: "5", // years
    
    // Tax Parameters
    filingStatus: "single",
    annualIncome: "85000",
    stateTaxRate: "7.5",
    country: "US",
    
    // Tax Efficiency Settings
    taxEfficiencyStrategy: "tax_loss_harvesting",
    useTaxAdvantaged: true,
    taxAdvantagedAmount: "2000",
    
    // Investment Details
    dividendYield: "2.0",
    turnoverRate: "15",
    expenseRatio: "0.25",
    
    // Additional Factors
    additionalContributions: "100",
    contributionFrequency: "monthly",
    reinvestDividends: true,
    
    // Withdrawal Strategy
    withdrawalRate: "4",
    withdrawalType: "percentage"
  });

  // Initialize result with default values to prevent undefined errors
  const [result, setResult] = useState({
    // Core Returns
    preTaxReturn: 0,
    postTaxReturn: 0,
    realPostTaxReturn: 0,
    inflationAdjustedReturn: 0,
    
    // Future Values
    preTaxFutureValue: 0,
    postTaxFutureValue: 0,
    realPostTaxFutureValue: 0,
    taxAdvantagedImpact: 0,
    
    // Tax Analysis
    taxCost: 0,
    taxEfficiency: 0,
    annualTaxDrag: 0,
    dividendTaxDrag: 0,
    turnoverTaxDrag: 0,
    totalTaxDrag: 0,
    
    // Breakdown
    dividendYield: 0,
    expenseRatio: 0,
    capitalGainsRate: 0,
    dividendTaxRate: 0,
    
    // Withdrawal Analysis
    withdrawalAnalysis: {
      annualWithdrawal: 0,
      withdrawalRate: 0,
      simpleYears: 0,
      sustainableYears: 0,
      portfolioDepletion: 'Low Risk'
    },
    
    // Additional Metrics
    investmentHorizon: 0,
    additionalContributions: 0,
    contributionFrequency: 'monthly',
    
    // Efficiency Scores
    taxEfficiencyScore: 'Poor',
    overallEfficiency: 0
  });

  const [calculationHistory, setCalculationHistory] = useState([]);
  const chartRef = useRef();

  // Tax efficiency multipliers for different strategies
  const taxEfficiencyStrategies = {
    buy_hold: 0.95, // 5% tax drag
    tax_loss_harvesting: 0.98, // 2% tax drag
    tax_managed: 0.99, // 1% tax drag
    municipal_bonds: 1.00, // 0% tax drag (federal)
    retirement_account: 1.00 // 0% tax drag (deferred)
  };

  // Asset class tax characteristics
  const assetClassTaxRates = {
    stocks: { ltcg: 0.15, stcg: 0.32, dividend: 0.15 },
    bonds: { ltcg: 0.22, stcg: 0.32, dividend: 0.32 },
    crypto: { ltcg: 0.20, stcg: 0.37, dividend: 0.00 },
    realestate: { ltcg: 0.15, stcg: 0.32, dividend: 0.15 },
    etf: { ltcg: 0.15, stcg: 0.32, dividend: 0.15 },
    muni_bonds: { ltcg: 0.00, stcg: 0.00, dividend: 0.00 }
  };

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "calculator") {
      calculatePostTaxReturns();
    }
  }, [inputs, mode, activeTab]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setInputs(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const calculatePostTaxReturns = () => {
    const {
      initialInvestment, currentValue, investmentType, holdingPeriod,
      expectedReturn, inflationRate, investmentHorizon,
      filingStatus, annualIncome, stateTaxRate,
      taxEfficiencyStrategy, useTaxAdvantaged, taxAdvantagedAmount,
      dividendYield, turnoverRate, expenseRatio,
      additionalContributions, contributionFrequency, reinvestDividends,
      withdrawalRate, withdrawalType
    } = inputs;

    // Parse inputs with proper defaults
    const initialInv = parseFloat(initialInvestment) || 0;
    const currentVal = parseFloat(currentValue) || 0;
    const expReturn = (parseFloat(expectedReturn) || 0) / 100;
    const inflation = (parseFloat(inflationRate) || 0) / 100;
    const horizon = parseFloat(investmentHorizon) || 1;
    const stateRate = (parseFloat(stateTaxRate) || 0) / 100;
    const divYield = (parseFloat(dividendYield) || 0) / 100;
    const turnover = (parseFloat(turnoverRate) || 0) / 100;
    const expense = (parseFloat(expenseRatio) || 0) / 100;
    const addContributions = parseFloat(additionalContributions) || 0;
    const taxAdvAmount = parseFloat(taxAdvantagedAmount) || 0;

    // Get asset class tax characteristics with fallbacks
    const assetTax = assetClassTaxRates[investmentType] || assetClassTaxRates.stocks;
    
    // Calculate tax drag components
    const isLongTerm = parseFloat(holdingPeriod) > 365;
    const capitalGainsRate = isLongTerm ? assetTax.ltcg : assetTax.stcg;
    const dividendTaxRate = assetTax.dividend;
    
    // Calculate annual tax drag
    const dividendTaxDrag = divYield * dividendTaxRate;
    const turnoverTaxDrag = turnover * capitalGainsRate * 0.5; // Assume 50% gains realized
    const totalTaxDrag = dividendTaxDrag + turnoverTaxDrag;
    
    // Apply tax efficiency strategy
    const efficiencyMultiplier = taxEfficiencyStrategies[taxEfficiencyStrategy] || 0.95;
    const adjustedTaxDrag = totalTaxDrag * (1 - efficiencyMultiplier);
    
    // Calculate pre-tax and post-tax returns
    const preTaxReturn = expReturn;
    const postTaxReturn = Math.max(0, preTaxReturn - adjustedTaxDrag - expense);
    const realPostTaxReturn = Math.max(0, postTaxReturn - inflation);
    
    // Calculate future values
    const preTaxFutureValue = calculateFutureValue(initialInv, preTaxReturn, horizon, addContributions, contributionFrequency);
    const postTaxFutureValue = calculateFutureValue(initialInv, postTaxReturn, horizon, addContributions, contributionFrequency);
    const realPostTaxFutureValue = calculateFutureValue(initialInv, realPostTaxReturn, horizon, addContributions, contributionFrequency);
    
    // Calculate tax-advantaged impact
    let taxAdvantagedImpact = 0;
    if (useTaxAdvantaged) {
      const taxAdvFutureValue = calculateFutureValue(taxAdvAmount, preTaxReturn, horizon, 0, 'none');
      taxAdvantagedImpact = taxAdvFutureValue - calculateFutureValue(taxAdvAmount, postTaxReturn, horizon, 0, 'none');
    }
    
    // Calculate withdrawal scenarios
    const withdrawalAnalysis = calculateWithdrawalAnalysis(postTaxFutureValue, withdrawalRate, withdrawalType, postTaxReturn);
    
    // Calculate tax efficiency metrics
    const taxCost = preTaxFutureValue - postTaxFutureValue;
    const taxEfficiency = preTaxReturn > 0 ? (postTaxReturn / preTaxReturn) * 100 : 0;
    const annualTaxDragPercent = adjustedTaxDrag * 100;

    const resultData = {
      // Core Returns
      preTaxReturn: preTaxReturn * 100,
      postTaxReturn: postTaxReturn * 100,
      realPostTaxReturn: realPostTaxReturn * 100,
      inflationAdjustedReturn: realPostTaxReturn * 100,
      
      // Future Values
      preTaxFutureValue,
      postTaxFutureValue,
      realPostTaxFutureValue,
      taxAdvantagedImpact,
      
      // Tax Analysis
      taxCost,
      taxEfficiency,
      annualTaxDrag: annualTaxDragPercent,
      dividendTaxDrag: dividendTaxDrag * 100,
      turnoverTaxDrag: turnoverTaxDrag * 100,
      totalTaxDrag: totalTaxDrag * 100,
      
      // Breakdown
      dividendYield: divYield * 100,
      expenseRatio: expense * 100,
      capitalGainsRate: capitalGainsRate * 100,
      dividendTaxRate: dividendTaxRate * 100,
      
      // Withdrawal Analysis
      withdrawalAnalysis,
      
      // Additional Metrics
      investmentHorizon: horizon,
      additionalContributions: addContributions,
      contributionFrequency,
      
      // Efficiency Scores
      taxEfficiencyScore: calculateEfficiencyScore(taxEfficiency),
      overallEfficiency: calculateOverallEfficiency(postTaxReturn, preTaxReturn, expense)
    };

    setResult(resultData);
  };

  const calculateFutureValue = (initial, returnRate, years, additional = 0, frequency = 'yearly') => {
    if (!initial || initial <= 0) return 0;
    
    let periods = years;
    let periodRate = returnRate;
    
    switch(frequency) {
      case 'monthly':
        periods = years * 12;
        periodRate = returnRate / 12;
        break;
      case 'quarterly':
        periods = years * 4;
        periodRate = returnRate / 4;
        break;
      default:
        periods = years;
        periodRate = returnRate;
    }
    
    let futureValue = initial * Math.pow(1 + periodRate, periods);
    
    if (additional > 0) {
      // Future value of annuity
      const fvAnnuity = additional * ((Math.pow(1 + periodRate, periods) - 1) / periodRate);
      futureValue += fvAnnuity;
    }
    
    return futureValue || 0;
  };

  const calculateWithdrawalAnalysis = (portfolioValue, withdrawalRate, withdrawalType, expectedReturn) => {
    const rate = (parseFloat(withdrawalRate) || 0) / 100;
    let annualWithdrawal;
    
    if (withdrawalType === 'percentage') {
      annualWithdrawal = portfolioValue * rate;
    } else {
      annualWithdrawal = parseFloat(withdrawalRate) || 0;
    }
    
    const withdrawalYears = portfolioValue / (annualWithdrawal || 1);
    const sustainableYears = calculateSustainableWithdrawal(portfolioValue, annualWithdrawal, expectedReturn);
    
    return {
      annualWithdrawal: annualWithdrawal || 0,
      withdrawalRate: ((annualWithdrawal || 0) / (portfolioValue || 1)) * 100,
      simpleYears: withdrawalYears || 0,
      sustainableYears: sustainableYears || 0,
      portfolioDepletion: sustainableYears < 30 ? 'High Risk' : sustainableYears < 50 ? 'Moderate Risk' : 'Low Risk'
    };
  };

  const calculateSustainableWithdrawal = (portfolio, withdrawal, returnRate) => {
    if (!portfolio || !withdrawal || withdrawal <= 0) return 0;
    
    // Simplified calculation - in practice this would be more complex
    if (withdrawal >= portfolio * returnRate) {
      return portfolio / withdrawal;
    }
    return 99; // Effectively infinite for low withdrawal rates
  };

  const calculateEfficiencyScore = (taxEfficiency) => {
    if (taxEfficiency >= 95) return 'Excellent';
    if (taxEfficiency >= 90) return 'Very Good';
    if (taxEfficiency >= 85) return 'Good';
    if (taxEfficiency >= 80) return 'Fair';
    return 'Poor';
  };

  const calculateOverallEfficiency = (postTaxReturn, preTaxReturn, expenseRatio) => {
    if (preTaxReturn <= 0) return 0;
    
    const taxEfficiency = postTaxReturn / preTaxReturn;
    const costEfficiency = 1 - (expenseRatio / preTaxReturn);
    return ((taxEfficiency + costEfficiency) / 2) * 100;
  };

  const calculateTaxEfficiencyComparison = () => {
    if (!result) return [];

    const strategies = [
      { name: "Buy & Hold", key: "buy_hold" },
      { name: "Tax Loss Harvesting", key: "tax_loss_harvesting" },
      { name: "Tax-Managed Funds", key: "tax_managed" },
      { name: "Municipal Bonds", key: "municipal_bonds" },
      { name: "Retirement Accounts", key: "retirement_account" }
    ];

    return strategies.map(strategy => {
      const efficiency = taxEfficiencyStrategies[strategy.key];
      const postTaxReturn = result.preTaxReturn - (result.totalTaxDrag * (1 - efficiency));
      const futureValue = calculateFutureValue(
        parseFloat(inputs.initialInvestment) || 0,
        postTaxReturn / 100,
        parseFloat(inputs.investmentHorizon) || 1
      );

      return {
        name: strategy.name,
        efficiency: efficiency * 100,
        postTaxReturn: postTaxReturn || 0,
        futureValue: futureValue || 0,
        taxSavings: (result.preTaxFutureValue - futureValue) || 0
      };
    });
  };

  const calculateContributionAnalysis = () => {
    const { initialInvestment, additionalContributions, contributionFrequency, investmentHorizon, expectedReturn } = inputs;
    
    const initial = parseFloat(initialInvestment) || 0;
    const additional = parseFloat(additionalContributions) || 0;
    const horizon = parseFloat(investmentHorizon) || 1;
    const returnRate = (parseFloat(expectedReturn) || 0) / 100;

    const scenarios = [
      { name: "No Additional Contributions", additional: 0 },
      { name: "Current Contribution Plan", additional: additional },
      { name: "Aggressive Savings", additional: additional * 2 },
      { name: "Maximum Savings", additional: additional * 3 }
    ];

    return scenarios.map(scenario => {
      const futureValue = calculateFutureValue(initial, returnRate, horizon, scenario.additional, contributionFrequency);
      const totalContributions = initial + (scenario.additional * getPeriodsPerYear(contributionFrequency) * horizon);
      const investmentGrowth = futureValue - totalContributions;

      return {
        name: scenario.name,
        futureValue: futureValue || 0,
        totalContributions: totalContributions || 0,
        investmentGrowth: investmentGrowth || 0,
        growthPercentage: totalContributions > 0 ? (investmentGrowth / totalContributions) * 100 : 0
      };
    });
  };

  const getPeriodsPerYear = (frequency) => {
    switch(frequency) {
      case 'monthly': return 12;
      case 'quarterly': return 4;
      case 'yearly': return 1;
      default: return 1;
    }
  };

  const resetCalculator = () => {
    setInputs({
      initialInvestment: "10000",
      currentValue: "15000",
      investmentType: "stocks",
      holdingPeriod: "365",
      expectedReturn: "12",
      inflationRate: "2.5",
      investmentHorizon: "5",
      filingStatus: "single",
      annualIncome: "85000",
      stateTaxRate: "7.5",
      country: "US",
      taxEfficiencyStrategy: "tax_loss_harvesting",
      useTaxAdvantaged: true,
      taxAdvantagedAmount: "2000",
      dividendYield: "2.0",
      turnoverRate: "15",
      expenseRatio: "0.25",
      additionalContributions: "100",
      contributionFrequency: "monthly",
      reinvestDividends: true,
      withdrawalRate: "4",
      withdrawalType: "percentage"
    });
    addNotification("Post-tax return calculator reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!result) return;
    
    const csvContent = [
      "Metric,Value",
      `Pre-tax Return,${(result.preTaxReturn || 0).toFixed(2)}%`,
      `Post-tax Return,${(result.postTaxReturn || 0).toFixed(2)}%`,
      `Real Post-tax Return,${(result.realPostTaxReturn || 0).toFixed(2)}%`,
      `Tax Efficiency,${(result.taxEfficiency || 0).toFixed(2)}%`,
      `Annual Tax Drag,${(result.annualTaxDrag || 0).toFixed(2)}%`,
      `Pre-tax Future Value,$${(result.preTaxFutureValue || 0).toFixed(2)}`,
      `Post-tax Future Value,$${(result.postTaxFutureValue || 0).toFixed(2)}`,
      `Tax Cost,$${(result.taxCost || 0).toFixed(2)}`,
      `Investment Horizon,${result.investmentHorizon || 0} years`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'post-tax-returns.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    addNotification("Post-tax return data exported to CSV successfully", "success");
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!result) return;
    
    const newCalculation = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      inputs: { ...inputs },
      result: { ...result }
    };
    
    setCalculationHistory(prev => [newCalculation, ...prev.slice(0, 9)]);
    addNotification("Post-tax return calculation saved to history", "success");
  };

  const loadCalculation = (calc) => {
    setInputs(calc.inputs);
    setResult(calc.result);
    setShowSaved(false);
    addNotification("Post-tax return calculation loaded successfully", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      conservative: { 
        investmentType: "bonds",
        expectedReturn: "5",
        taxEfficiencyStrategy: "municipal_bonds"
      },
      moderate: { 
        investmentType: "stocks",
        expectedReturn: "8",
        taxEfficiencyStrategy: "tax_loss_harvesting"
      },
      aggressive: {
        investmentType: "stocks",
        expectedReturn: "12",
        taxEfficiencyStrategy: "tax_managed"
      },
      retirement: {
        investmentType: "etf",
        expectedReturn: "7",
        taxEfficiencyStrategy: "retirement_account",
        useTaxAdvantaged: true
      }
    };
    
    setInputs(prev => ({ ...prev, ...presets[preset] }));
    addNotification(`${preset} investment strategy applied`, "info");
  };

  // Chart Data with safe access
  const returnComparisonChartData = {
    labels: ["Pre-tax Return", "Post-tax Return", "Real Post-tax Return"],
    datasets: [{
      data: [
        result?.preTaxReturn || 0,
        result?.postTaxReturn || 0,
        result?.realPostTaxReturn || 0
      ],
      backgroundColor: ["#10B981", "#3B82F6", "#6366F1"],
      borderWidth: 2,
      hoverOffset: 15
    }],
  };

  const taxDragBreakdownChartData = {
    labels: ["Dividend Tax Drag", "Turnover Tax Drag", "Expense Ratio", "Net Return"],
    datasets: [{
      data: [
        result?.dividendTaxDrag || 0,
        result?.turnoverTaxDrag || 0,
        result?.expenseRatio || 0,
        result?.postTaxReturn || 0
      ],
      backgroundColor: ["#EF4444", "#F59E0B", "#8B5CF6", "#10B981"],
      borderWidth: 2
    }],
  };

  const efficiencyComparisonData = calculateTaxEfficiencyComparison();
  const efficiencyChartData = {
    labels: efficiencyComparisonData.map(s => s.name),
    datasets: [
      {
        label: 'Post-tax Return (%)',
        data: efficiencyComparisonData.map(s => s.postTaxReturn),
        backgroundColor: "#3B82F6",
        borderColor: "#3B82F6",
        borderWidth: 2
      },
      {
        label: 'Tax Efficiency (%)',
        data: efficiencyComparisonData.map(s => s.efficiency),
        backgroundColor: "#10B981",
        borderColor: "#10B981",
        borderWidth: 2
      }
    ]
  };

  const contributionAnalysisData = calculateContributionAnalysis();
  const contributionChartData = {
    labels: contributionAnalysisData.map(s => s.name),
    datasets: [
      {
        label: 'Future Value',
        data: contributionAnalysisData.map(s => s.futureValue),
        backgroundColor: "#8B5CF6",
        borderColor: "#8B5CF6",
        borderWidth: 2
      }
    ]
  };

  return (
    <div className={'profit-calculator'}>
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>Post-Tax Return Calculator</h1>
        </div>
        <p>Calculate your true investment returns after taxes, inflation, and expenses</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      {/* Saved Calculations Modal */}
      {showSaved && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Saved Return Calculations</h3>
              <button className="close-btn" onClick={() => setShowSaved(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="saved-list">
              {savedCalculations.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-bookmark fa-2x"></i>
                  <p>No saved calculations yet</p>
                </div>
              ) : (
                savedCalculations.map(calc => (
                  <div key={calc.id} className="saved-item" onClick={() => loadCalculation(calc)}>
                    <div className="saved-name">{calc.name}</div>
                    <div className="saved-details">
                      <span className={`profit ${(calc.result?.postTaxReturn || 0) >= 0 ? 'positive' : 'negative'}`}>
                        {(calc.result?.postTaxReturn || 0).toFixed(2)}%
                      </span>
                      <span className="date">{calc.timestamp}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "calculator" ? "active" : ""}`}
                onClick={() => setActiveTab("calculator")}
              >
                <i className="fas fa-calculator"></i>
                {isMobile ? 'Calculator' : 'Return Calculator'}
              </button>
              <button 
                className={`tab ${activeTab === "efficiency" ? "active" : ""}`}
                onClick={() => setActiveTab("efficiency")}
              >
                <i className="fas fa-bolt"></i>
                {isMobile ? 'Efficiency' : 'Tax Efficiency'}
              </button>
              <button 
                className={`tab ${activeTab === "contributions" ? "active" : ""}`}
                onClick={() => setActiveTab("contributions")}
              >
                <i className="fas fa-piggy-bank"></i>
                {isMobile ? 'Contributions' : 'Contributions'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Quick Presets */}
          <div className="presets-section">
            <h4>Investment Strategies:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('conservative')}>
                Conservative
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('moderate')}>
                Moderate
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('aggressive')}>
                Aggressive
              </button>
              <button className="preset-btn conservative" onClick={() => quickPreset('retirement')}>
                Retirement
              </button>
            </div>
          </div>

          {/* Calculator Tab */}
          {activeTab === "calculator" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  <h3>
                    <i className="fas fa-chart-line"></i>
                    Investment Basics
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-money-bill-wave"></i> Initial Investment</label>
                      <input 
                        type="number" 
                        name="initialInvestment" 
                        value={inputs.initialInvestment} 
                        onChange={handleChange}
                        placeholder="10000"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-chart-line"></i> Expected Return (%)</label>
                      <input 
                        type="number" 
                        name="expectedReturn" 
                        value={inputs.expectedReturn} 
                        onChange={handleChange}
                        placeholder="8.0"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-calendar"></i> Investment Horizon (Years)</label>
                      <input 
                        type="number" 
                        name="investmentHorizon" 
                        value={inputs.investmentHorizon} 
                        onChange={handleChange}
                        placeholder="5"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-money-bill-wave"></i> Investment Type</label>
                      <select name="investmentType" value={inputs.investmentType} onChange={handleChange}>
                        <option value="stocks">Stocks</option>
                        <option value="bonds">Bonds</option>
                        <option value="crypto">Cryptocurrency</option>
                        <option value="realestate">Real Estate</option>
                        <option value="etf">ETF/Mutual Fund</option>
                        <option value="muni_bonds">Municipal Bonds</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Dividend Yield (%)</label>
                      <input 
                        type="number" 
                        name="dividendYield" 
                        value={inputs.dividendYield} 
                        onChange={handleChange}
                        placeholder="2.0"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-exchange-alt"></i> Portfolio Turnover (%)</label>
                      <input 
                        type="number" 
                        name="turnoverRate" 
                        value={inputs.turnoverRate} 
                        onChange={handleChange}
                        placeholder="15"
                        step="0.1"
                      />
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-file-invoice-dollar"></i>
                    Tax & Expense Parameters
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-user"></i> Filing Status</label>
                      <select name="filingStatus" value={inputs.filingStatus} onChange={handleChange}>
                        <option value="single">Single</option>
                        <option value="married">Married Filing Jointly</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-money-bill"></i> Annual Income</label>
                      <input 
                        type="number" 
                        name="annualIncome" 
                        value={inputs.annualIncome} 
                        onChange={handleChange}
                        placeholder="85000"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> State Tax Rate (%)</label>
                      <input 
                        type="number" 
                        name="stateTaxRate" 
                        value={inputs.stateTaxRate} 
                        onChange={handleChange}
                        placeholder="7.5"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-cog"></i> Expense Ratio (%)</label>
                      <input 
                        type="number" 
                        name="expenseRatio" 
                        value={inputs.expenseRatio} 
                        onChange={handleChange}
                        placeholder="0.25"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-bolt"></i> Tax Efficiency Strategy</label>
                      <select name="taxEfficiencyStrategy" value={inputs.taxEfficiencyStrategy} onChange={handleChange}>
                        <option value="buy_hold">Buy & Hold</option>
                        <option value="tax_loss_harvesting">Tax Loss Harvesting</option>
                        <option value="tax_managed">Tax-Managed Funds</option>
                        <option value="municipal_bonds">Municipal Bonds</option>
                        <option value="retirement_account">Retirement Accounts</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-chart-line"></i> Inflation Rate (%)</label>
                      <input 
                        type="number" 
                        name="inflationRate" 
                        value={inputs.inflationRate} 
                        onChange={handleChange}
                        placeholder="2.5"
                        step="0.1"
                      />
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-piggy-bank"></i>
                    Additional Contributions
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Additional Contributions</label>
                      <input 
                        type="number" 
                        name="additionalContributions" 
                        value={inputs.additionalContributions} 
                        onChange={handleChange}
                        placeholder="100"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-calendar"></i> Contribution Frequency</label>
                      <select name="contributionFrequency" value={inputs.contributionFrequency} onChange={handleChange}>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="yearly">Yearly</option>
                        <option value="none">None</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label className="checkbox-label">
                        <input 
                          type="checkbox" 
                          name="reinvestDividends" 
                          checked={inputs.reinvestDividends} 
                          onChange={handleChange}
                        />
                        Reinvest Dividends
                      </label>
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-umbrella"></i>
                    Withdrawal Planning
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Withdrawal Rate</label>
                      <input 
                        type="number" 
                        name="withdrawalRate" 
                        value={inputs.withdrawalRate} 
                        onChange={handleChange}
                        placeholder="4.0"
                        step="0.1"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-money-bill-wave"></i> Withdrawal Type</label>
                      <select name="withdrawalType" value={inputs.withdrawalType} onChange={handleChange}>
                        <option value="percentage">Percentage of Portfolio</option>
                        <option value="fixed">Fixed Amount</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save to History
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* Efficiency Tab */}
          {activeTab === "efficiency" && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-bolt"></i>
                  Tax Efficiency Strategies
                </h3>
                <p>Compare different tax efficiency strategies and their impact on your returns</p>
                
                {efficiencyComparisonData && efficiencyComparisonData.length > 0 && (
                  <div className="strategy-list">
                    {efficiencyComparisonData.map((strategy, index) => (
                      <div key={index} className="strategy-item">
                        <div className="strategy-header">
                          <span className="strategy-name">{strategy.name}</span>
                          <span className="strategy-efficiency">{strategy.efficiency.toFixed(1)}%</span>
                        </div>
                        <div className="strategy-stats">
                          <div className="strategy-stat">
                            <span>Post-tax Return:</span>
                            <span>{strategy.postTaxReturn.toFixed(2)}%</span>
                          </div>
                          <div className="strategy-stat">
                            <span>Future Value:</span>
                            <span>${strategy.futureValue.toFixed(2)}</span>
                          </div>
                          <div className="strategy-stat">
                            <span>Tax Savings:</span>
                            <span>${strategy.taxSavings.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Contributions Tab */}
          {activeTab === "contributions" && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-piggy-bank"></i>
                  Contribution Analysis
                </h3>
                <p>See how different contribution strategies impact your long-term wealth</p>
                
                {contributionAnalysisData && contributionAnalysisData.length > 0 && (
                  <div className="strategy-list">
  {contributionAnalysisData.map((scenario, index) => (
    <div key={index} className="strategy-item">
      <div className="strategy-header">
        <span className="strategy-name">{scenario.name}</span>
        <span className="strategy-efficiency">${scenario.futureValue.toFixed(2)}</span>
      </div>
      <div className="strategy-stats">
        <div className="strategy-stat">
          <span>Total Contributions:</span>
          <span>${scenario.totalContributions.toFixed(2)}</span>
        </div>
        <div className="strategy-stat">
          <span>Investment Growth:</span>
          <span>${scenario.investmentGrowth.toFixed(2)}</span>
        </div>
        <div className="strategy-stat">
          <span>Growth %:</span>
          <span>{scenario.growthPercentage.toFixed(2)}%</span>
        </div>
      </div>
    </div>
  ))}
</div>

                )}
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Recent Return Calculations
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No return calculations yet. Start calculating to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className={`history-profit ${(calc.result?.postTaxReturn || 0) >= 0 ? 'positive' : 'negative'}`}>
                          <i className={`fas ${(calc.result?.postTaxReturn || 0) >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'}`}></i>
                          {(calc.result?.postTaxReturn || 0).toFixed(2)}%
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-chart-line"></i> Pre-tax: {(calc.result?.preTaxReturn || 0).toFixed(2)}%</span>
                        <span><i className="fas fa-percentage"></i> Efficiency: {(calc.result?.taxEfficiency || 0).toFixed(2)}%</span>
                        <span><i className="fas fa-calendar"></i> {calc.result?.investmentHorizon || 0} years</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "calculator" || activeTab === "efficiency" || activeTab === "contributions") && (
          <div className="right-panel">
            {activeTab === "calculator" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className="fas fa-chart-bar"></i>
                    Post-Tax Return Analysis
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {result ? (
                  <>
                    <div className="results-grid">
                      <div className="result-card highlight">
                        <div className={`result-value ${(result.postTaxReturn || 0) >= 0 ? 'positive' : 'negative'}`}>
                          {(result.postTaxReturn || 0).toFixed(2)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-chart-line"></i>
                          Post-tax Return
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {(result.preTaxReturn || 0).toFixed(2)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-chart-line"></i>
                          Pre-tax Return
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {(result.taxEfficiency || 0).toFixed(2)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-bolt"></i>
                          Tax Efficiency
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {(result.realPostTaxReturn || 0).toFixed(2)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-inflation"></i>
                          Real Return
                        </div>
                      </div>
                    </div>

                    <div className="detailed-results">
                      <h4>Return Comparison</h4>
                      <div className="detail-row">
                        <span><i className="fas fa-chart-line"></i> Pre-tax Annual Return:</span>
                        <span>{(result.preTaxReturn || 0).toFixed(2)}%</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-file-invoice-dollar"></i> Annual Tax Drag:</span>
                        <span>-{(result.annualTaxDrag || 0).toFixed(2)}%</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-cog"></i> Expense Ratio:</span>
                        <span>-{(result.expenseRatio || 0).toFixed(2)}%</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-chart-line"></i> Post-tax Annual Return:</span>
                        <span>{(result.postTaxReturn || 0).toFixed(2)}%</span>
                      </div>
                      <div className="detail-row highlight">
                        <span><i className="fas fa-inflation"></i> Real Post-tax Return (after inflation):</span>
                        <span className={(result.realPostTaxReturn || 0) >= 0 ? 'positive' : 'negative'}>
                          {(result.realPostTaxReturn || 0).toFixed(2)}%
                        </span>
                      </div>
                    </div>

                    <div className="detailed-results">
                      <h4>Future Value Projection</h4>
                      <div className="detail-row">
                        <span><i className="fas fa-piggy-bank"></i> Initial Investment:</span>
                        <span>${(result.initialInvestment || 0).toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-rocket"></i> Pre-tax Future Value ({(result.investmentHorizon || 0)} years):</span>
                        <span>${(result.preTaxFutureValue || 0).toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-file-invoice-dollar"></i> Total Tax Cost:</span>
                        <span>-${(result.taxCost || 0).toFixed(2)}</span>
                      </div>
                      <div className="detail-row highlight">
                        <span><i className="fas fa-money-bill-wave"></i> Post-tax Future Value:</span>
                        <span>${(result.postTaxFutureValue || 0).toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Return Comparison Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-bar"></i>
                        Return Comparison
                      </h3>
                      <Bar 
                        data={returnComparisonChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              display: false
                            }
                          },
                          scales: {
                            y: {
                              beginAtZero: true,
                              title: {
                                display: true,
                                text: 'Annual Return (%)'
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Tax Drag Breakdown Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-file-invoice-dollar"></i>
                        Return Composition
                      </h3>
                      <Doughnut 
                        data={taxDragBreakdownChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              position: isMobile ? 'top' : 'bottom'
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return `${context.label}: ${context.parsed.toFixed(2)}%`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Withdrawal Analysis */}
                    {result.withdrawalAnalysis && (
                      <div className="risk-summary">
                        <h4>Withdrawal Analysis</h4>
                        <div className="risk-cards">
                          <div className="risk-card">
                            <div className="risk-value">
                              ${(result.withdrawalAnalysis.annualWithdrawal || 0).toFixed(2)}
                            </div>
                            <div className="risk-label">Annual Withdrawal</div>
                          </div>
                          <div className="risk-card">
                            <div className="risk-value">
                              {(result.withdrawalAnalysis.withdrawalRate || 0).toFixed(2)}%
                            </div>
                            <div className="risk-label">Withdrawal Rate</div>
                          </div>
                          <div className="risk-card">
                            <div className="risk-value">
                              {(result.withdrawalAnalysis.sustainableYears || 0).toFixed(1)}
                            </div>
                            <div className="risk-label">Sustainable Years</div>
                          </div>
                          <div className="risk-card">
                            <div className={`risk-value ${result.withdrawalAnalysis.portfolioDepletion === 'High Risk' ? 'negative' : result.withdrawalAnalysis.portfolioDepletion === 'Moderate Risk' ? 'moderate' : 'positive'}`}>
                              {result.withdrawalAnalysis.portfolioDepletion}
                            </div>
                            <div className="risk-label">Portfolio Risk</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Efficiency Scores */}
                    <div className="detailed-results">
                      <h4>Efficiency Assessment</h4>
                      <div className="detail-row">
                        <span><i className="fas fa-bolt"></i> Tax Efficiency Score:</span>
                        <span className={(result.taxEfficiency || 0) >= 90 ? 'positive' : (result.taxEfficiency || 0) >= 80 ? 'moderate' : 'negative'}>
                          {result.taxEfficiencyScore}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-chart-line"></i> Overall Efficiency:</span>
                        <span>{(result.overallEfficiency || 0).toFixed(2)}%</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-percentage"></i> Dividend Tax Drag:</span>
                        <span>{(result.dividendTaxDrag || 0).toFixed(2)}%</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-exchange-alt"></i> Turnover Tax Drag:</span>
                        <span>{(result.turnoverTaxDrag || 0).toFixed(2)}%</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-calculator fa-3x"></i>
                    <p>Enter your investment details to see post-tax return calculations</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "efficiency" && efficiencyComparisonData && efficiencyComparisonData.length > 0 && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-bolt"></i>
                  Tax Efficiency Comparison
                </h3>
                <div className="chart-container">
                  <Bar 
                    data={efficiencyChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          display: true,
                          position: 'top'
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Percentage (%)'
                          }
                        }
                      }
                    }}
                  />
                </div>
                <div className="efficiency-summary">
                  <h4>Efficiency Analysis</h4><br/>
                  <div className="summary-grid">
                    <div className="summary-item">
                      <span>Most Efficient Strategy:</span>
                      <span>{efficiencyComparisonData.reduce((max, s) => s.efficiency > max.efficiency ? s : max).name}</span>
                    </div>
                    <div className="summary-item">
                      <span>Highest Post-tax Return:</span>
                      <span>{efficiencyComparisonData.reduce((max, s) => s.postTaxReturn > max.postTaxReturn ? s : max).name}</span>
                    </div>
                    <div className="summary-item">
                      <span>Potential Tax Savings:</span>
                      <span>${Math.max(...efficiencyComparisonData.map(s => s.taxSavings)).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "contributions" && contributionAnalysisData && contributionAnalysisData.length > 0 && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-piggy-bank"></i>
                  Contribution Impact Analysis
                </h3>
                <div className="chart-container">
                  <Bar 
                    data={contributionChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          display: false
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            callback: function(value) {
                              return '$' + value.toLocaleString();
                            }
                          }
                        }
                      }
                    }}
                  />
                </div>
                <div className="contribution-summary">
                  <h4>Contribution Analysis</h4><br/>
                  <div className="summary-grid">
                    <div className="summary-item">
                      <span>Highest Future Value:</span>
                      <span>${Math.max(...contributionAnalysisData.map(s => s.futureValue)).toFixed(2)}</span>
                    </div>
                    <div className="summary-item">
                      <span>Best Growth Percentage:</span>
                      <span>{Math.max(...contributionAnalysisData.map(s => s.growthPercentage)).toFixed(2)}%</span>
                    </div>
                    <div className="summary-item">
                      <span>Additional Growth from Contributions:</span>
                      <span>${(contributionAnalysisData[1]?.futureValue - contributionAnalysisData[0]?.futureValue || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default PostTaxReturnCalculator;